import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isElectron, isCapacitor } from '../utils/platform';

const RecorderPanelContext = createContext(null);

export function RecorderPanelProvider({ children }) {
  // Panel is only available on web (not Electron or Capacitor)
  const isWebOnly = !isElectron && !isCapacitor;

  const [isOpen, setIsOpen] = useState(false);
  // Mode: 'recorder' for regular dictaphone, 'live' for live recorder
  const [panelMode, setPanelMode] = useState('recorder');

  const location = useLocation();

  // Close panel when navigating away from supported pages (reports, home)
  useEffect(() => {
    const supportsSidePanel = location.pathname.startsWith('/reports') || location.pathname.startsWith('/home');
    if (!supportsSidePanel) {
      setIsOpen(false);
    }
  }, [location.pathname]);

  const openPanel = useCallback((mode = 'recorder') => {
    if (!isWebOnly) return;
    setPanelMode(mode);
    setIsOpen(true);
  }, [isWebOnly]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePanel = useCallback((mode = 'recorder') => {
    if (!isWebOnly) return;

    if (isOpen && panelMode === mode) {
      // Same mode clicked while open - close it
      setIsOpen(false);
    } else {
      // Different mode or closed - open with new mode
      setPanelMode(mode);
      setIsOpen(true);
    }
  }, [isWebOnly, isOpen, panelMode]);

  const value = {
    isOpen,
    panelMode,
    isWebOnly,
    openPanel,
    closePanel,
    togglePanel,
  };

  return (
    <RecorderPanelContext.Provider value={value}>
      {children}
    </RecorderPanelContext.Provider>
  );
}

export function useRecorderPanel() {
  const context = useContext(RecorderPanelContext);
  if (!context) {
    // Return a safe default if used outside provider (e.g., during tests)
    return {
      isOpen: false,
      panelMode: 'recorder',
      isWebOnly: false,
      openPanel: () => {},
      closePanel: () => {},
      togglePanel: () => {},
    };
  }
  return context;
}

export default RecorderPanelContext;
